import { cmsGraphql, FragmentType, getFragmentData } from '../../lib/cms/index.js'

export const videoEmbedTypename = 'CmsVideoEmbedBlock'

export const videoEmbedBlockFragment = cmsGraphql(/* GraphQL */ `
  fragment videoEmbedBlockFragment on CmsVideoEmbedBlock {
    embedType
    embedUrl
  }
`)

export type VideoEmbedBlockProps = {
  data: FragmentType<typeof videoEmbedBlockFragment>
}

// Helpers to extract video IDs
const extractYouTubeID = (url: string) => {
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/)
  return match ? match[1] : null
}

const extractVimeoID = (url: string) => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  return match ? match[1] : null
}

const extractWistiaID = (url: string) => {
  const match = url.match(/wistia\.com\/medias\/(\w+)/)
  return match ? match[1] : null
}

const sanitizeId = (id: string | null | undefined) => {
  if (!id) return null
  return id.match(/^[a-zA-Z0-9-_]+$/) ? id : null
}

const getEmbedSrc = (embedUrl: string, embedType: 'youtube' | 'vimeo' | 'wistia') => {
  if (!embedUrl || !embedType) {
    return null
  }

  switch (embedType) {
    case 'youtube': {
      const ytId = sanitizeId(extractYouTubeID(embedUrl))
      return ytId ? `https://www.youtube.com/embed/${ytId}` : null
    }
    case 'vimeo': {
      const vimeoId = sanitizeId(extractVimeoID(embedUrl))
      return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : null
    }
    case 'wistia': {
      const wistiaId = sanitizeId(extractWistiaID(embedUrl))
      return wistiaId ? `https://fast.wistia.net/embed/iframe/${wistiaId}` : null
    }
    default:
      return null
  }
}

export const VideoEmbedBlock = ({ data }: VideoEmbedBlockProps) => {
  const { embedType, embedUrl } = getFragmentData(videoEmbedBlockFragment, data)

  if (!embedType || !embedUrl) {
    return null
  }

  const src = getEmbedSrc(embedUrl, embedType)

  if (!src) return null

  return (
    <div className="aspect-[16/9]">
      <iframe
        className="size-full"
        src={src}
        title="Embedded video"
        frameBorder="0"
        allowFullScreen
        allow="autoplay; encrypted-media"
      />
    </div>
  )
}
